import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {LoginService} from '../../services/login.service';
import {CommonService} from '../../services/common.service';
import {AuthService} from '../../services/auth.service';
import environment from '../../../environment';

type DirectSsoPayload = {
  apiToken: string | null;
  cleanPath: string;
  hasTokens: boolean;
};

@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  public form: FormGroup;
  public loading = false;
  public message: string | null = null;
  constructor(
    public fb: FormBuilder,
    private loginService: LoginService,
    private commonService: CommonService,
    private authService: AuthService
  ) {}

  async ngOnInit() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
    const state = history.state;
    this.message = state?.message || null;

    // Clear the state so it's not there on reload
    if (this.message) {
      history.replaceState({}, document.title);
    }

    // Check for direct SSO tokens in loginRedirect
    await this.checkForSsoTokens();
  }

  /**
   * Accept direct SSO API tokens and redirect back to the requested page.
   */
  private async checkForSsoTokens(): Promise<void> {
    const loginRedirect = localStorage.getItem('loginRedirect');
    const redirectPayload = this.parseSsoPayload(loginRedirect);
    const currentPayload = this.parseSsoPayload(window.location.href);

    const tokenPayload = redirectPayload?.hasTokens
      ? redirectPayload
      : currentPayload?.hasTokens
      ? currentPayload
      : null;

    if (!tokenPayload) {
      if (loginRedirect && redirectPayload) {
        localStorage.setItem('loginRedirect', redirectPayload.cleanPath);
      }
      if (!loginRedirect) {
        if (environment.settings.verboseLogging) {
          console.log('[PreviewAuth] No direct SSO tokens found');
        }
      }
      return;
    }

    const {apiToken} = tokenPayload;

    if (!apiToken) {
      if (environment.settings.verboseLogging) {
        console.warn('[PreviewAuth] Missing direct SSO access token');
      }
      return;
    }

    this.authService.applyStandardSession(apiToken);

    if (loginRedirect && redirectPayload?.hasTokens) {
      localStorage.setItem('loginRedirect', redirectPayload.cleanPath);
    }

    const redirectTarget =
      loginRedirect && redirectPayload?.cleanPath
        ? redirectPayload.cleanPath
        : tokenPayload.cleanPath;

    if (loginRedirect) {
      // IMPORTANT: Clear loginRedirect BEFORE navigating to prevent loops
      localStorage.removeItem('loginRedirect');
      if (environment.settings.verboseLogging) {
        console.log('[PreviewAuth] Cleared loginRedirect');
      }
    } else {
      window.history.replaceState({}, document.title, tokenPayload.cleanPath);
    }

    const finalRedirect =
      redirectTarget && redirectTarget.includes('/Login')
        ? '/'
        : redirectTarget;

    if (finalRedirect) {
      if (environment.settings.verboseLogging) {
        console.log('[PreviewAuth] Redirecting to:', finalRedirect);
      }
      // Use window.location for navigation to properly handle query params
      window.location.href = finalRedirect;
    }
  }

  async onSubmit() {
    try {
      if (this.form.invalid) {
        return;
      }
      this.loading = true;

      await this.loginService
        .awsLogin(
          this.form.getRawValue().email,
          this.form.getRawValue().password
        )
        .then(res => this.handleAWSLoginSuccess(res));
      this.loading = false;

      const redirect = localStorage.getItem('loginRedirect');
      if (redirect) {
        localStorage.removeItem('loginRedirect');
        this.commonService.navigate(redirect);
      } else {
        this.commonService.navigate('/');
      }
    } catch (error) {
      document.getElementById('warning').innerText =
        error?.error?.error || 'Something went wrong';
      this.loading = false;
    }
  }

  handleAWSLoginSuccess(response) {
    this.authService.applyStandardSession(
      response.token,
      response.refreshToken
    );
  }

  private parseSsoPayload(urlString: string | null): DirectSsoPayload | null {
    if (!urlString) {
      return null;
    }

    try {
      const url = new URL(urlString, window.location.origin);
      const tokenKeys = ['apiToken', 'token', 'refreshToken'];
      const queryParams = url.searchParams;
      const rawHash = url.hash ? url.hash.slice(1) : '';
      const hasHashParams = rawHash.includes('=') || rawHash.includes('&');
      const hashParams = hasHashParams ? new URLSearchParams(rawHash) : null;
      const apiToken = this.getSsoParam(hashParams, queryParams, 'apiToken');
      const rawToken = this.getSsoParam(hashParams, queryParams, 'token');
      const resolvedApiToken = apiToken || rawToken;
      const hasTokens = Boolean(resolvedApiToken);

      if (hasTokens) {
        tokenKeys.forEach(key => {
          queryParams.delete(key);
          hashParams?.delete(key);
        });
      }

      let cleanedHash = rawHash;
      if (hashParams) {
        cleanedHash = hashParams.toString();
      }

      url.hash = cleanedHash ? `#${cleanedHash}` : '';
      const cleanPath = `${url.pathname}${url.search}${url.hash}`;

      return {
        apiToken: resolvedApiToken,
        cleanPath,
        hasTokens,
      };
    } catch (error) {
      if (environment.settings.verboseLogging) {
        console.warn('[PreviewAuth] Invalid redirect URL:', urlString, error);
      }
      return null;
    }
  }

  private getSsoParam(
    hashParams: URLSearchParams | null,
    queryParams: URLSearchParams,
    key: string
  ): string | null {
    return hashParams?.get(key) || queryParams.get(key);
  }
}
